import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { 
  ArrowLeft, 
  Save, 
  Edit, 
  Trash2, 
  UserPlus, 
  Medal, 
  RefreshCw, 
  Check, 
  X, 
  ChevronDown,
  Plus
} from 'lucide-react';
import { Competition, EventReferee, Result } from '../../types/models';
import { getCompetitionById, updateCompetitionStatus } from '../../services/competitionService';
import { getEventReferees, assignReferee, removeRefereeAssignment } from '../../services/refereeAssignmentService';
import { getRegistrationsByEvent, updateRegistrationStatus, updateMassRegistrationStatus } from '../../services/registrationService';
import { getResultsByCompetition, createResult, deleteResult, updateResult, createTeamResult, deleteTeamResult, updateTeamResult } from '../../services/resultService';
import { getReferees } from '../../services/refereeService';
import { getHouses } from '../../services/houseService';
import { exportCompetitionToPDF } from '../../utils/export';

const CompetitionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [referees, setReferees] = useState<EventReferee[]>([]);
  const [availableReferees, setAvailableReferees] = useState<any[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [houses, setHouses] = useState<any[]>([]);
  const [teamRegistrations, setTeamRegistrations] = useState<any[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canManage, setCanManage] = useState(false);
  
  const [newReferee, setNewReferee] = useState<{
    referee_id: string;
    role: 'head' | 'assistant' | 'timekeeper' | 'scorer';
  }>({
    referee_id: '',
    role: 'head'
  });
  
  const [savingReferee, setSavingReferee] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  const [updatingAllStatus, setUpdatingAllStatus] = useState(false);
  const [massStatus, setMassStatus] = useState<string>('');
  const [showMassStatusDropdown, setShowMassStatusDropdown] = useState(false);
  
  const [editingParticipant, setEditingParticipant] = useState<string | null>(null);
  const [participantPosition, setParticipantPosition] = useState<number | null>(null);
  const [savingParticipantPosition, setSavingParticipantPosition] = useState(false);
  
  // Team event states
  const [showTeamRegistrationForm, setShowTeamRegistrationForm] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState<string>('');
  const [savingTeamRegistration, setSavingTeamRegistration] = useState(false);
  
  // Team results states
  const [editingTeamResult, setEditingTeamResult] = useState<string | null>(null);
  const [teamPosition, setTeamPosition] = useState<number | null>(null);
  const [savingTeamPosition, setSavingTeamPosition] = useState(false);

  useEffect(() => {
    if (id) {
      loadCompetitionData();
      checkPermissions();
    }
  }, [id]);

  const checkPermissions = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasManageAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setCanManage(hasManageAccess);
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadCompetitionData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [competitionData, refereesData, participantsData, resultsData, housesData] = await Promise.all([
        getCompetitionById(id),
        getEventReferees(id),
        getRegistrationsByEvent(id),
        getResultsByCompetition(id),
        getHouses()
      ]);
      
      setCompetition(competitionData);
      setReferees(refereesData);
      setParticipants(participantsData || []);
      setResults(resultsData);
      setHouses(housesData || []);
      
      // Load available referees
      const allReferees = await getReferees();
      const assignedRefereeIds = refereesData.map(r => r.referee_id);
      const available = allReferees.filter(r => 
        !assignedRefereeIds.includes(r.referee_id) && r.is_active
      );
      setAvailableReferees(available);
      
      // Set initial status
      if (competitionData) {
        setSelectedStatus(competitionData.status);
      }
      
      // Load team registrations if this is a team event
      if (competitionData?.is_team_event) {
        const { data: teamRegs, error: teamRegsError } = await supabase
          .from('team_registrations')
          .select(`
            *,
            house:houses(*)
          `)
          .eq('event_id', id);
          
        if (teamRegsError) throw teamRegsError;
        setTeamRegistrations(teamRegs || []);
      }
      
    } catch (error: any) {
      console.error('Error loading competition data:', error);
      setError(error.message || 'Failed to load competition data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignReferee = async () => {
    if (!id || !newReferee.referee_id || !newReferee.role) return;
    
    try {
      setSavingReferee(true);
      setError(null);
      
      await assignReferee({
        event_id: id,
        referee_id: newReferee.referee_id,
        role: newReferee.role
      });
      
      // Reset form and reload data
      setNewReferee({
        referee_id: '',
        role: 'head'
      });
      
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error assigning referee:', error);
      setError(error.message || 'Failed to assign referee');
    } finally {
      setSavingReferee(false);
    }
  };

  const handleRemoveReferee = async (assignmentId: string) => {
    if (!id) return;
    
    try {
      setError(null);
      await removeRefereeAssignment(assignmentId);
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error removing referee:', error);
      setError(error.message || 'Failed to remove referee');
    }
  };

  const handleStatusChange = async (status: Competition['status']) => {
    if (!id || !competition) return;
    
    try {
      setChangingStatus(true);
      setError(null);
      
      await updateCompetitionStatus(id, status);
      setCompetition({ ...competition, status });
      setSelectedStatus(status);
      setShowStatusDropdown(false);
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.message || 'Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleUpdateAllStatus = async () => {
    if (!id || !massStatus) return;
    
    try {
      setUpdatingAllStatus(true);
      setError(null);
      
      await updateMassRegistrationStatus(id, massStatus as any);
      await loadCompetitionData();
      setMassStatus('');
      setShowMassStatusDropdown(false);
    } catch (error: any) {
      console.error('Error updating all statuses:', error);
      setError(error.message || 'Failed to update statuses');
    } finally {
      setUpdatingAllStatus(false);
    }
  };

  const handleUpdateParticipantStatus = async (registrationId: string, status: string) => {
    try {
      setError(null);
      await updateRegistrationStatus(registrationId, status as any);
      
      // Update local state
      setParticipants(prev => 
        prev.map(p => 
          p.registration_id === registrationId 
            ? { ...p, status } 
            : p
        )
      );
    } catch (error: any) {
      console.error('Error updating participant status:', error);
      setError(error.message || 'Failed to update participant status');
    }
  };

  const startEditParticipant = (resultId: string, position: number | null) => {
    setEditingParticipant(resultId);
    setParticipantPosition(position);
  };

  const cancelEditParticipant = () => {
    setEditingParticipant(null);
    setParticipantPosition(null);
  };

  const saveParticipantPosition = async (participant: any, existingResultId?: string) => {
    if (!id || participantPosition === null || !competition) return;
    
    try {
      setSavingParticipantPosition(true);
      setError(null);
      
      // Calculate points based on position
      let points = competition.points_allocation.participation || 1; // Default to participation points
      if (participantPosition === 1) points = competition.points_allocation["1"] || 5;
      else if (participantPosition === 2) points = competition.points_allocation["2"] || 3;
      else if (participantPosition === 3) points = competition.points_allocation["3"] || 2;
      
      if (existingResultId) {
        // Update existing result
        await updateResult(existingResultId, {
          position: participantPosition,
          points_earned: points,
          verified_by: 'manual_entry'
        });
      } else {
        // Create new result
        await createResult({
          event_id: id,
          student_id: participant.student_id,
          age_group_id: competition.age_group_id || '',
          competition_year: competition.competition_year,
          position: participantPosition,
          points_earned: points,
          verified_by: 'manual_entry'
        });
      }
      
      // Reset form and reload data
      cancelEditParticipant();
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error saving participant position:', error);
      setError(error.message || 'Failed to save participant position');
    } finally {
      setSavingParticipantPosition(false);
    }
  };

  const handleDeleteResult = async (resultId: string) => {
    if (!window.confirm('Are you sure you want to delete this result?')) {
      return;
    }
    
    try {
      setError(null);
      await deleteResult(resultId);
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error deleting result:', error);
      setError(error.message || 'Failed to delete result');
    }
  };

  const handleDeleteTeamResult = async (resultId: string) => {
    if (!window.confirm('Are you sure you want to delete this team result?')) {
      return;
    }
    
    try {
      setError(null);
      await deleteTeamResult(resultId);
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error deleting team result:', error);
      setError(error.message || 'Failed to delete team result');
    }
  };

  const hasResult = (studentId: string) => {
    return results.some(r => r.student_id === studentId);
  };

  const getParticipantResult = (studentId: string) => {
    return results.find(r => r.student_id === studentId);
  };

  const handleExportPDF = async () => {
    if (!competition) return;
    
    try {
      await exportCompetitionToPDF(competition, referees, results);
    } catch (error: any) {
      console.error('Error exporting to PDF:', error);
      setError(error.message || 'Failed to export to PDF');
    }
  };

  // Team registration functions
  const handleRegisterTeam = async () => {
    if (!id || !selectedHouse || !competition) return;
    
    try {
      setSavingTeamRegistration(true);
      setError(null);
      
      // Check if team is already registered
      const existingRegistration = teamRegistrations.find(reg => reg.house_id === selectedHouse);
      if (existingRegistration) {
        setError('This team is already registered for this competition');
        return;
      }
      
      // Create team registration
      const { data, error: regError } = await supabase
        .from('team_registrations')
        .insert({
          event_id: id,
          house_id: selectedHouse,
          competition_year: competition.competition_year,
          status: 'registered'
        })
        .select(`
          *,
          house:houses(*)
        `)
        .single();
      
      if (regError) throw regError;
      
      // Update local state
      setTeamRegistrations(prev => [...prev, data]);
      setSelectedHouse('');
      setShowTeamRegistrationForm(false);
      
    } catch (error: any) {
      console.error('Error registering team:', error);
      setError(error.message || 'Failed to register team');
    } finally {
      setSavingTeamRegistration(false);
    }
  };

  const handleUpdateTeamStatus = async (registrationId: string, status: string) => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from('team_registrations')
        .update({ status })
        .eq('registration_id', registrationId);
      
      if (error) throw error;
      
      // Update local state
      setTeamRegistrations(prev => 
        prev.map(reg => 
          reg.registration_id === registrationId 
            ? { ...reg, status } 
            : reg
        )
      );
    } catch (error: any) {
      console.error('Error updating team status:', error);
      setError(error.message || 'Failed to update team status');
    }
  };

  const handleDeleteTeamRegistration = async (registrationId: string) => {
    if (!window.confirm('Are you sure you want to delete this team registration?')) {
      return;
    }
    
    try {
      setError(null);
      
      const { error } = await supabase
        .from('team_registrations')
        .delete()
        .eq('registration_id', registrationId);
      
      if (error) throw error;
      
      // Update local state
      setTeamRegistrations(prev => prev.filter(reg => reg.registration_id !== registrationId));
    } catch (error: any) {
      console.error('Error deleting team registration:', error);
      setError(error.message || 'Failed to delete team registration');
    }
  };

  // Team results functions
  const startEditTeamResult = (resultId: string, position: number | null) => {
    setEditingTeamResult(resultId);
    setTeamPosition(position);
  };

  const cancelEditTeamResult = () => {
    setEditingTeamResult(null);
    setTeamPosition(null);
  };

  const saveTeamResult = async (houseId: string, existingResultId?: string) => {
    if (!id || !competition || teamPosition === null) return;
    
    try {
      setSavingTeamPosition(true);
      setError(null);
      
      // Calculate points based on position
      let points = competition.points_allocation.participation || 1; // Default to participation points
      if (teamPosition === 1) points = competition.points_allocation["1"] || 5;
      else if (teamPosition === 2) points = competition.points_allocation["2"] || 3;
      else if (teamPosition === 3) points = competition.points_allocation["3"] || 2;
      
      if (existingResultId) {
        // Update existing team result
        await updateTeamResult(existingResultId, {
          position: teamPosition,
          points_earned: points,
          verified_by: 'manual_entry'
        });
      } else {
        // Create new team result
        await createTeamResult({
          event_id: id,
          house_id: houseId,
          competition_year: competition.competition_year,
          position: teamPosition,
          points_earned: points,
          verified_by: 'manual_entry'
        });
      }
      
      // Reset form and reload data
      cancelEditTeamResult();
      await loadCompetitionData();
    } catch (error: any) {
      console.error('Error saving team result:', error);
      setError(error.message || 'Failed to save team result');
    } finally {
      setSavingTeamPosition(false);
    }
  };

  const hasTeamResult = (houseId: string) => {
    return results.some(r => r.student_id === houseId);
  };

  const getTeamResult = (houseId: string) => {
    return results.find(r => r.student_id === houseId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{t('competitions.details.errors.notFound')}</div>
        <button
          onClick={() => navigate('/competitions')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('competitions.actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{competition.event_name}</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                {competition.competition_year}
              </span>
            </div>
            <div className="flex items-center mt-1 space-x-2">
              <div className="text-sm font-mono text-blue-600">
                {t('competitions.details.sections.details.ref')}: {competition.reference_number}
              </div>
              <div className="text-sm font-mono text-blue-600">
                {t('mobileResults.verificationCodePlaceholder')}: {competition.verification_code}
              </div>
              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                competition.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                'bg-green-100 text-green-800'
              }`}>
                {t(`competitions.status.${competition.status}`)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          {canManage && (
            <>
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={changingStatus}
                >
                  {changingStatus ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t('competitions.actions.updating')}
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      {t('competitions.actions.changeStatus')}
                    </>
                  )}
                </button>
                
                {showStatusDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu">
                      <button
                        onClick={() => handleStatusChange('not_started')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.not_started')}
                      </button>
                      <button
                        onClick={() => handleStatusChange('in_progress')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.in_progress')}
                      </button>
                      <button
                        onClick={() => handleStatusChange('completed')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.completed')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {!competition.is_team_event && (
                <Link
                  to={`/competitions/${id}/register`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('competitions.actions.registerParticipants')}
                </Link>
              )}
              
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Medal className="h-4 w-4 mr-2" />
                {t('competitions.actions.export')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('competitions.details.sections.details.title')}
          </h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.category')}</h4>
              <p className="mt-1 text-sm text-gray-900">{competition.category?.category_name}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.ageGroup')}</h4>
              <p className="mt-1 text-sm text-gray-900">
                {competition.age_group?.group_name || 'All Age Groups'}
                {competition.age_group && (
                  <span className="text-xs text-gray-500 block">
                    {t('competitions.details.sections.details.born')}: {competition.age_group.birth_years.join(', ')}
                  </span>
                )}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.gender')}</h4>
              <p className="mt-1 text-sm text-gray-900">
                {competition.gender_category === 'M' ? t('competitions.filters.male') :
                 competition.gender_category === 'F' ? t('competitions.filters.female') :
                 t('competitions.filters.all')}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.type')}</h4>
              <p className="mt-1 text-sm text-gray-900">
                {competition.is_team_event ? 
                  t('competitions.details.sections.details.teamEvent') : 
                  t('competitions.details.sections.details.individualEvent')}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.pointsAllocation')}</h4>
              <div className="mt-1 text-sm">
                <div>1st: <span className="font-medium">{competition.points_allocation["1"]} {t('competitions.details.sections.details.points')}</span></div>
                <div>2nd: <span className="font-medium">{competition.points_allocation["2"]} {t('competitions.details.sections.details.points')}</span></div>
                <div>3rd: <span className="font-medium">{competition.points_allocation["3"]} {t('competitions.details.sections.details.points')}</span></div>
                <div>Participation: <span className="font-medium">{competition.points_allocation.participation} {t('competitions.details.sections.details.points')}</span></div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">{t('competitions.details.sections.details.year')}</h4>
              <p className="mt-1 text-sm text-gray-900">{competition.competition_year}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('competitions.details.sections.referees.title')}
          </h3>
        </div>
        <div className="border-t border-gray-200">
          {referees.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24\" stroke="currentColor">
                  <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t('competitions.details.sections.referees.noReferees.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('competitions.details.sections.referees.noReferees.description')}
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.referees.table.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.referees.table.tamilName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.referees.table.role')}
                  </th>
                  {canManage && (
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {referees.map((referee) => (
                  <tr key={referee.assignment_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {referee.referee?.first_name} {referee.referee?.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {referee.referee?.first_name_tamil} {referee.referee?.last_name_tamil}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        referee.role === 'head' ? 'bg-green-100 text-green-800' :
                        referee.role === 'assistant' ? 'bg-blue-100 text-blue-800' :
                        referee.role === 'timekeeper' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {referee.role === 'head' ? t('competitions.details.sections.referees.assignReferee.fields.role.head') :
                         referee.role === 'assistant' ? t('competitions.details.sections.referees.assignReferee.fields.role.assistant') :
                         referee.role === 'timekeeper' ? t('competitions.details.sections.referees.assignReferee.fields.role.timekeeper') :
                         t('competitions.details.sections.referees.assignReferee.fields.role.scorer')}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRemoveReferee(referee.assignment_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {canManage && availableReferees.length > 0 && (
            <div className="px-4 py-5 sm:p-6 border-t border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t('competitions.details.sections.referees.assignReferee.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="referee_id" className="block text-sm font-medium text-gray-700">
                    {t('competitions.details.sections.referees.assignReferee.fields.referee.label')}
                  </label>
                  <select
                    id="referee_id"
                    value={newReferee.referee_id}
                    onChange={(e) => setNewReferee({ ...newReferee, referee_id: e.target.value })}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">{t('competitions.details.sections.referees.assignReferee.fields.referee.placeholder')}</option>
                    {availableReferees.map((referee) => (
                      <option key={referee.referee_id} value={referee.referee_id}>
                        {referee.first_name} {referee.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    {t('competitions.details.sections.referees.assignReferee.fields.role.label')}
                  </label>
                  <select
                    id="role"
                    value={newReferee.role}
                    onChange={(e) => setNewReferee({ ...newReferee, role: e.target.value as any })}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="head">{t('competitions.details.sections.referees.assignReferee.fields.role.head')}</option>
                    <option value="assistant">{t('competitions.details.sections.referees.assignReferee.fields.role.assistant')}</option>
                    <option value="timekeeper">{t('competitions.details.sections.referees.assignReferee.fields.role.timekeeper')}</option>
                    <option value="scorer">{t('competitions.details.sections.referees.assignReferee.fields.role.scorer')}</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleAssignReferee}
                    disabled={!newReferee.referee_id || savingReferee}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                  >
                    {savingReferee ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    {t('competitions.details.sections.referees.assignReferee.assign')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Team Registrations Section (only for team events) */}
      {competition.is_team_event && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('teamRegistrations.title')}
            </h3>
            
            {canManage && (
              <button
                onClick={() => setShowTeamRegistrationForm(!showTeamRegistrationForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('teamRegistrations.registerTeam')}
              </button>
            )}
          </div>
          
          {showTeamRegistrationForm && (
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="house_id" className="block text-sm font-medium text-gray-700">
                    {t('teamRegistrations.selectHouse')}
                  </label>
                  <select
                    id="house_id"
                    value={selectedHouse}
                    onChange={(e) => setSelectedHouse(e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">{t('teamRegistrations.selectHousePlaceholder')}</option>
                    {houses.map((house) => (
                      <option key={house.house_id} value={house.house_id}>
                        {house.house_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleRegisterTeam}
                    disabled={!selectedHouse || savingTeamRegistration}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                  >
                    {savingTeamRegistration ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    {t('teamRegistrations.registerTeam')}
                  </button>
                  <button
                    onClick={() => setShowTeamRegistrationForm(false)}
                    className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t('teamRegistrations.table.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t border-gray-200">
            {teamRegistrations.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24\" stroke="currentColor">
                    <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t('teamRegistrations.noTeams')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('teamRegistrations.noTeamsDescription')}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('teamRegistrations.table.house')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('teamRegistrations.table.status')}
                    </th>
                    {canManage && (
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('teamRegistrations.table.position')}
                      </th>
                    )}
                    {canManage && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('teamRegistrations.table.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamRegistrations.map((registration) => {
                    const teamResult = getTeamResult(registration.house_id);
                    const hasResultRecorded = !!teamResult;
                    
                    return (
                      <tr key={registration.registration_id} className={hasResultRecorded ? 'bg-green-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="h-3 w-3 rounded-full mr-2" 
                              style={{ backgroundColor: registration.house?.house_color }}
                            />
                            <div className="text-sm font-medium text-gray-900">
                              {registration.house?.house_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {hasResultRecorded ? (
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t('competitions.details.sections.participants.resultRecorded')}
                              </span>
                            </div>
                          ) : (
                            <select
                              value={registration.status}
                              onChange={(e) => handleUpdateTeamStatus(registration.registration_id, e.target.value)}
                              className="block w-full pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                              disabled={!canManage}
                            >
                              <option value="registered">{t('participants.form.registrations.status.registered')}</option>
                              <option value="confirmed">{t('participants.form.registrations.status.confirmed')}</option>
                              <option value="withdrawn">{t('participants.form.registrations.status.withdrawn')}</option>
                              <option value="disqualified">{t('participants.form.registrations.status.disqualified')}</option>
                            </select>
                          )}
                        </td>
                        {canManage && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {hasResultRecorded ? (
                              <div className="flex items-center space-x-2">
                                <div className="text-sm font-medium">
                                  {teamResult?.position || t('competitions.details.sections.results.participation')}
                                </div>
                                <button
                                  onClick={() => startEditTeamResult(teamResult?.result_id || '', teamResult?.position || null)}
                                  className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md text-sm"
                                >
                                  {t('teamRegistrations.editPosition')}
                                </button>
                              </div>
                            ) : registration.status === 'confirmed' && (
                              <div>
                                {editingTeamResult === registration.house_id ? (
                                  <div className="flex items-center space-x-2">
                                    <select
                                      value={teamPosition || ''}
                                      onChange={(e) => setTeamPosition(e.target.value ? parseInt(e.target.value) : null)}
                                      className="w-20 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                      <option value="">{t('teamRegistrations.selectPosition')}</option>
                                      <option value="1">1</option>
                                      <option value="2">2</option>
                                      <option value="3">3</option>
                                      <option value="0">P</option>
                                    </select>
                                    <button
                                      onClick={() => saveTeamResult(registration.house_id)}
                                      disabled={savingTeamPosition || teamPosition === null}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <Check className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={cancelEditTeamResult}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <X className="h-5 w-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => startEditTeamResult(registration.house_id, null)}
                                    className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md text-sm"
                                  >
                                    {t('teamRegistrations.addPosition')}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {canManage && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleDeleteTeamRegistration(registration.registration_id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={hasResultRecorded}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Individual Participants Section (only for individual events) */}
      {!competition.is_team_event && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('competitions.details.sections.participants.title')}
            </h3>
            
            {canManage && participants.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowMassStatusDropdown(!showMassStatusDropdown)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('competitions.details.sections.participants.updateAllStatus.title')}
                  <ChevronDown className="ml-2 h-4 w-4" />
                </button>
                
                {showMassStatusDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setMassStatus('registered');
                          setShowMassStatusDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('participants.form.registrations.status.registered')}
                      </button>
                      <button
                        onClick={() => {
                          setMassStatus('confirmed');
                          setShowMassStatusDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('participants.form.registrations.status.confirmed')}
                      </button>
                      <button
                        onClick={() => {
                          setMassStatus('withdrawn');
                          setShowMassStatusDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('participants.form.registrations.status.withdrawn')}
                      </button>
                      <button
                        onClick={() => {
                          setMassStatus('disqualified');
                          setShowMassStatusDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('participants.form.registrations.status.disqualified')}
                      </button>
                    </div>
                    
                    {massStatus && (
                      <div className="px-4 py-3 border-t border-gray-100">
                        <button
                          onClick={handleUpdateAllStatus}
                          disabled={updatingAllStatus}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                        >
                          {updatingAllStatus ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              {t('competitions.details.sections.participants.updateAllStatus.updating')}
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              {t('competitions.details.sections.participants.updateAllStatus.update')}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="border-t border-gray-200">
            {participants.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24\" stroke="currentColor">
                    <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t('competitions.details.sections.participants.noParticipants.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('competitions.details.sections.participants.noParticipants.description')}
                </p>
                {canManage && (
                  <div className="mt-6">
                    <Link
                      to={`/competitions/${id}/register`}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {t('competitions.actions.registerParticipants')}
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.details.sections.participants.table.name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.details.sections.participants.table.birthYear')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.details.sections.participants.table.house')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.details.sections.participants.table.status')}
                    </th>
                    {canManage && (
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('competitions.details.sections.participants.table.position')}
                      </th>
                    )}
                    {canManage && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('competitions.details.sections.results.table.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {participants.map((participant) => {
                    const participantResult = getParticipantResult(participant.student_id);
                    const hasResultRecorded = !!participantResult;
                    
                    return (
                      <tr key={participant.registration_id} className={hasResultRecorded ? 'bg-green-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {participant.student?.first_name} {participant.student?.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {participant.student?.first_name_tamil} {participant.student?.last_name_tamil}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(participant.student?.date_of_birth).getFullYear()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="h-3 w-3 rounded-full mr-2" 
                              style={{ backgroundColor: participant.student?.house?.house_color }}
                            />
                            <div className="text-sm text-gray-900">
                              {participant.student?.house?.house_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {hasResultRecorded ? (
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t('competitions.details.sections.participants.resultRecorded')}
                              </span>
                            </div>
                          ) : (
                            <select
                              value={participant.status}
                              onChange={(e) => handleUpdateParticipantStatus(participant.registration_id, e.target.value)}
                              className="block w-full pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                              disabled={!canManage}
                            >
                              <option value="registered">{t('participants.form.registrations.status.registered')}</option>
                              <option value="confirmed">{t('participants.form.registrations.status.confirmed')}</option>
                              <option value="withdrawn">{t('participants.form.registrations.status.withdrawn')}</option>
                              <option value="disqualified">{t('participants.form.registrations.status.disqualified')}</option>
                            </select>
                          )}
                        </td>
                        {canManage && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {hasResultRecorded ? (
                              <div className="flex items-center space-x-2">
                                <div className="text-sm font-medium">
                                  {participantResult?.position || t('competitions.details.sections.results.participation')}
                                </div>
                                <button
                                  onClick={() => startEditParticipant(participantResult?.result_id || '', participantResult?.position || null)}
                                  className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md text-sm"
                                >
                                  {t('competitions.details.sections.results.editPosition')}
                                </button>
                              </div>
                            ) : participant.status === 'confirmed' && (
                              <div>
                                {editingParticipant === participant.registration_id ? (
                                  <div className="flex items-center space-x-2">
                                    <select
                                      value={participantPosition || ''}
                                      onChange={(e) => setParticipantPosition(e.target.value ? parseInt(e.target.value) : null)}
                                      className="w-20 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                      <option value="">{t('competitions.details.sections.results.select')}</option>
                                      <option value="1">1</option>
                                      <option value="2">2</option>
                                      <option value="3">3</option>
                                      <option value="0">P</option>
                                    </select>
                                    <button
                                      onClick={() => saveParticipantPosition(participant)}
                                      disabled={savingParticipantPosition || participantPosition === null}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <Check className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={cancelEditParticipant}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <X className="h-5 w-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => startEditParticipant(participant.registration_id, null)}
                                    className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md text-sm"
                                  >
                                    {t('competitions.details.sections.results.addPosition')}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {canManage && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {!hasResultRecorded && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this participant?')) {
                                    // Delete participant registration
                                    supabase
                                      .from('student_event_registrations')
                                      .delete()
                                      .eq('registration_id', participant.registration_id)
                                      .then(() => {
                                        // Refresh participants list
                                        setParticipants(prev => prev.filter(p => p.registration_id !== participant.registration_id));
                                      })
                                      .catch(error => {
                                        console.error('Error deleting participant:', error);
                                        setError('Failed to delete participant');
                                      });
                                  }
                                }}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('competitions.details.sections.results.title')}
          </h3>
        </div>
        <div className="border-t border-gray-200">
          {results.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24\" stroke="currentColor">
                  <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t('competitions.details.sections.results.noResults.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('competitions.details.sections.results.noResults.description')}
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.results.table.position')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {competition.is_team_event ? 'House' : t('competitions.details.sections.results.table.student')}
                  </th>
                  {!competition.is_team_event && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.details.sections.results.table.birthYear')}
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.results.table.house')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.results.table.points')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.details.sections.results.table.verifiedBy')}
                  </th>
                  {canManage && (
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">{t('competitions.details.sections.results.table.actions')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result) => (
                  <tr key={result.result_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingTeamResult === result.result_id || editingParticipant === result.result_id ? (
                        <div className="flex items-center space-x-2">
                          <select
                            value={competition.is_team_event ? teamPosition || '' : participantPosition || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : null;
                              if (competition.is_team_event) {
                                setTeamPosition(value);
                              } else {
                                setParticipantPosition(value);
                              }
                            }}
                            className="w-20 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">{t('competitions.details.sections.results.select')}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="0">P</option>
                          </select>
                          <button
                            onClick={() => {
                              if (competition.is_team_event) {
                                saveTeamResult(result.student_id, result.result_id);
                              } else {
                                // Find the participant for this result
                                const participant = participants.find(p => p.student_id === result.student_id);
                                if (participant) {
                                  saveParticipantPosition(participant, result.result_id);
                                }
                              }
                            }}
                            disabled={(competition.is_team_event ? savingTeamPosition : savingParticipantPosition) || 
                                     (competition.is_team_event ? teamPosition === null : participantPosition === null)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              if (competition.is_team_event) {
                                cancelEditTeamResult();
                              } else {
                                cancelEditParticipant();
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div className={`text-sm font-medium ${
                          result.position === 1 ? 'text-yellow-600' :
                          result.position === 2 ? 'text-gray-500' :
                          result.position === 3 ? 'text-amber-700' :
                          'text-gray-900'
                        }`}>
                          {result.position || t('competitions.details.sections.results.participation')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.student?.first_name} {result.student?.last_name}
                      </div>
                      {!competition.is_team_event && (
                        <div className="text-sm text-gray-500">
                          {result.student?.first_name_tamil} {result.student?.last_name_tamil}
                        </div>
                      )}
                    </td>
                    {!competition.is_team_event && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {result.student?.date_of_birth ? new Date(result.student.date_of_birth).getFullYear() : '-'}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="h-3 w-3 rounded-full mr-2" 
                          style={{ backgroundColor: result.student?.house?.house_color }}
                        />
                        <div className="text-sm text-gray-900">
                          {result.student?.house?.house_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.points_earned}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {result.verified_by === 'mobile_results' 
                          ? t('competitions.details.sections.results.verifiedBy.mobileResults')
                          : t('competitions.details.sections.results.verifiedBy.manualEntry')}
                      </div>
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() => {
                              if (competition.is_team_event) {
                                startEditTeamResult(result.result_id, result.position);
                              } else {
                                startEditParticipant(result.result_id, result.position);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md"
                          >
                            {t('competitions.details.sections.results.editPosition')}
                          </button>
                          <button
                            onClick={() => {
                              if (competition.is_team_event) {
                                handleDeleteTeamResult(result.result_id);
                              } else {
                                handleDeleteResult(result.result_id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitionDetails;